import { forwardRef } from "react";
import { CustomTellescopeFormFieldProps } from "../../types";
import { useForm } from "../../../use-form";
import { Button, cn, Input, Textarea } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { AnswerForType } from "@tellescope/types-models";

type BelugaPatientPreferenceMedicationValue = NonNullable<AnswerForType["Beluga Patient Preference"]>[number];

export interface BelugaPatientPreferenceMedicationProps
  extends CustomTellescopeFormFieldProps<BelugaPatientPreferenceMedicationValue> {
  onRemove: () => void;
  onBlur?: () => void;
}

export const BelugaPatientPreferenceMedication = forwardRef<HTMLDivElement, BelugaPatientPreferenceMedicationProps>(
  ({ value, onChange, onRemove, className, onBlur, ...rest }, ref) => {
    const { t } = useTranslation("forms");
    const form = useForm({
      fields: {
        name: {
          type: "text",
          required: true,
          initialValue: value.name,
        },
        strength: {
          type: "text",
          required: true,
          initialValue: value.strength,
        },
        dispenseUnit: {
          type: "text",
          required: true,
          initialValue: value.dispenseUnit,
        },
        quantity: {
          type: "text",
          required: true,
          initialValue: value.quantity,
        },
        refills: {
          type: "text",
          required: true,
          initialValue: value.refills,
        },
        daysSupply: {
          type: "text",
          required: true,
          initialValue: value.daysSupply,
        },
        sig: {
          type: "text",
          required: true,
          initialValue: value.sig,
        },
        medId: {
          type: "text",
          required: true,
          initialValue: value.medId,
        },
      },
      validateOn: "blur",
      sideEffects: (newValues) => {
        onChange(newValues);
        return newValues;
      },
    });

    const getFieldProps: typeof form.getFieldProps = (fieldName) => {
      const props = form.getFieldProps(fieldName);

      return {
        ...props,
        onBlur: () => {
          props.onBlur();
          onBlur?.();
        },
      };
    };

    return (
      <div className={cn("space-y-2 pb-4 mb-2 border-b", className)} {...rest} ref={ref}>
        <Input {...getFieldProps("name")} placeholder={t("Medication Name")} className="w-full" />
        <div className="flex gap-2 w-full">
          <Input {...getFieldProps("strength")} placeholder={t("Strength")} />
          <Input {...getFieldProps("dispenseUnit")} placeholder={t("Dispense Unit")} />
        </div>
        <div className="flex gap-2 w-full">
          <Input {...getFieldProps("quantity")} placeholder={t("Quantity")} />
          <Input {...getFieldProps("refills")} placeholder={t("Refills")} />
          <Input {...getFieldProps("daysSupply")} placeholder={t("Days Supply")} />
        </div>
        <Textarea {...getFieldProps("sig")} placeholder={t("Sig (Instructions)")} className="w-full" />
        <Input {...getFieldProps("medId")} placeholder={t("Med ID (NDC11)")} className="w-full" />
        <div className="flex justify-end">
          <Button variant="outline" onClick={onRemove}>
            {t("Remove Medication")}
          </Button>
        </div>
      </div>
    );
  }
);
